import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { DeleteOutline, DragIndicator } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import { SUPPORTED_IMAGE_ACCEPT, SUPPORTED_IMAGE_FORMAT_LABEL } from '../constants/imageUpload';
import { Bedroom } from '../types/bedroom';
import { AmenityId } from '../types/amenity';
import { BedroomEditor } from './BedroomEditor';
import { AmenitiesSelector } from './AmenitiesSelector';

const getErrorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  if (typeof message === 'string') {
    return message;
  }

  return fallback;
};

interface HouseDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  house?: {
    id: string;
    name: string;
    shortDescription?: string;
    description?: string;
    address: string;
    city?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    maxOccupants?: number;
    maxConsecutiveDays?: number;
    maxBookingLeadDays?: number;
    contributionCost?: number;
    contributionDescription?: string;
    bedrooms?: Bedroom[];
    amenities?: AmenityId[];
    images?: Array<{
      key: string;
      url: string;
      originalName: string;
    }>;
  };
}

export const HouseDialog: React.FC<HouseDialogProps> = ({ open, onClose, onSuccess, house }) => {
  const [formData, setFormData] = useState({
    name: '',
    shortDescription: '',
    description: '',
    address: '',
    city: '',
    country: '',
    latitude: '',
    longitude: '',
    maxOccupants: '',
    maxConsecutiveDays: '',
    maxBookingLeadDays: '',
    contributionCost: '',
    contributionDescription: '',
  });
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<NonNullable<HouseDialogProps['house']>['images']>([]);
  const [bedrooms, setBedrooms] = useState<Bedroom[]>([]);
  const [amenities, setAmenities] = useState<AmenityId[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (house) {
      setFormData({
        name: house.name || '',
        shortDescription: house.shortDescription || '',
        description: house.description || '',
        address: house.address || '',
        city: house.city || '',
        country: house.country || '',
        latitude: house.latitude?.toString() || '',
        longitude: house.longitude?.toString() || '',
        maxOccupants: house.maxOccupants?.toString() || '',
        maxConsecutiveDays: house.maxConsecutiveDays?.toString() || '',
        maxBookingLeadDays: house.maxBookingLeadDays?.toString() || '',
        contributionCost: house.contributionCost?.toString() || '',
        contributionDescription: house.contributionDescription || '',
      });
      setExistingImages(house.images || []);
      setBedrooms(house.bedrooms || []);
      setAmenities((house.amenities || []) as AmenityId[]);
    } else {
      // Reset form for new house
      setFormData({
        name: '',
        shortDescription: '',
        description: '',
        address: '',
        city: '',
        country: '',
        latitude: '',
        longitude: '',
        maxOccupants: '',
        maxConsecutiveDays: '',
        maxBookingLeadDays: '',
        contributionCost: '',
        contributionDescription: '',
      });
      setExistingImages([]);
      setBedrooms([]);
      setAmenities([]);
    }
    setSelectedImages([]);
    setError(null);
  }, [house, open]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedImages(prevImages => [...prevImages, ...Array.from(event.target.files || [])]);
  };

  const handleRemoveSelectedImage = (imageName: string) => {
    setSelectedImages(currentImages => currentImages.filter(image => image.name !== imageName));
  };

  const handleRemoveExistingImage = (imageKey: string) => {
    setExistingImages(currentImages => currentImages?.filter(image => image.key !== imageKey) || []);
  };

  const dragIndexRef = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;

    setExistingImages(currentImages => {
      if (!currentImages) return currentImages;
      const reordered = [...currentImages];
      const [dragged] = reordered.splice(dragIndexRef.current!, 1);
      reordered.splice(index, 0, dragged);
      dragIndexRef.current = index;
      return reordered;
    });
  };

  const buildHouseFormData = () => {
    const payload = new FormData();

    payload.append('name', formData.name);
    payload.append('address', formData.address);

    payload.append('shortDescription', formData.shortDescription);
    payload.append('description', formData.description);
    payload.append('city', formData.city);
    payload.append('country', formData.country);
    payload.append('latitude', formData.latitude);
    payload.append('longitude', formData.longitude);
    payload.append('maxOccupants', formData.maxOccupants);
    payload.append('maxConsecutiveDays', formData.maxConsecutiveDays);
    payload.append('maxBookingLeadDays', formData.maxBookingLeadDays);
    payload.append('contributionCost', formData.contributionCost);
    payload.append('contributionDescription', formData.contributionDescription);
    payload.append('bedrooms', JSON.stringify(bedrooms));
    payload.append('amenities', JSON.stringify(amenities));

    if (house?.images) {
      const removedImageKeys = house.images
        .filter(image => !existingImages?.some(existingImage => existingImage.key === image.key))
        .map(image => image.key);

      removedImageKeys.forEach(key => payload.append('removedImageKeys', key));
    }

    if (existingImages?.length) {
      existingImages.forEach(image => payload.append('existingImageKeys', image.key));
    }

    selectedImages.forEach(image => payload.append('images', image));

    return payload;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (house) {
        await axios.put(`/houses/${house.id}`, buildHouseFormData());
      } else {
        await axios.post('/houses', buildHouseFormData());
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, t('houseDialog.failedSave')));
      console.error('Error saving house:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{house ? t('houseDialog.editTitle') : t('houseDialog.createTitle')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.labels.houseName')}
                value={formData.name}
                onChange={handleChange('name')}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.fields.shortDescription')}
                value={formData.shortDescription}
                onChange={handleChange('shortDescription')}
                disabled={loading}
                inputProps={{ maxLength: 120 }}
                helperText={t('houseDialog.shortDescriptionHelper')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.fields.description')}
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Button component="label" variant="outlined" disabled={loading} fullWidth>
                {house ? t('houseDialog.addMorePhotos') : t('houseDialog.addPhotos')}
                <input hidden type="file" accept={SUPPORTED_IMAGE_ACCEPT} multiple onChange={handleImagesChange} />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('common.labels.supportedFormats', { formats: SUPPORTED_IMAGE_FORMAT_LABEL })}
              </Typography>
            </Grid>

            {existingImages && existingImages.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('common.labels.currentPhotos')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {t('common.labels.dragToReorder')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {existingImages.map((image, index) => (
                    <Box
                      key={image.key}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={() => { dragIndexRef.current = null; }}
                      sx={{
                        position: 'relative',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        opacity: 1,
                        transition: 'transform 0.15s ease',
                        '&:hover': { transform: 'scale(1.03)' },
                      }}
                    >
                      <Box
                        component="img"
                        src={image.url}
                        alt={image.originalName || formData.name}
                        sx={{
                          width: 120,
                          height: 90,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: index === 0 ? '2px solid' : 'none',
                          borderColor: 'primary.main',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          opacity: 0.8,
                        }}
                      >
                        <DragIndicator sx={{ fontSize: 16 }} />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveExistingImage(image.key)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {selectedImages.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('common.labels.newPhotosToUpload')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {selectedImages.map((image) => (
                    <Alert
                      key={`${image.name}-${image.size}`}
                      severity="info"
                      action={
                        <Button color="inherit" size="small" onClick={() => handleRemoveSelectedImage(image.name)}>
                          {t('houseDialog.remove')}
                        </Button>
                      }
                      sx={{ m: 0 }}
                    >
                      {image.name}
                    </Alert>
                  ))}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.fields.address')}
                value={formData.address}
                onChange={handleChange('address')}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.fields.city')}
                value={formData.city}
                onChange={handleChange('city')}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.fields.country')}
                value={formData.country}
                onChange={handleChange('country')}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.labels.latitude')}
                value={formData.latitude}
                onChange={handleChange('latitude')}
                type="number"
                inputProps={{ step: 'any' }}
                disabled={loading}
                helperText={t('common.helpers.optionalCoordinates')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.labels.longitude')}
                value={formData.longitude}
                onChange={handleChange('longitude')}
                type="number"
                inputProps={{ step: 'any' }}
                disabled={loading}
                helperText={t('common.helpers.optionalCoordinates')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('houseDialog.maxOccupants')}
                value={formData.maxOccupants}
                onChange={handleChange('maxOccupants')}
                type="number"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.labels.maxConsecutiveDays')}
                value={formData.maxConsecutiveDays}
                onChange={handleChange('maxConsecutiveDays')}
                type="number"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.labels.maxBookingLeadDays')}
                value={formData.maxBookingLeadDays}
                onChange={handleChange('maxBookingLeadDays')}
                type="number"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('common.labels.contribution')}
                value={formData.contributionCost}
                onChange={handleChange('contributionCost')}
                type="number"
                inputProps={{ step: '0.01' }}
                disabled={loading}
                helperText={t('common.helpers.contributionPerStay')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.labels.contributionDescription')}
                value={formData.contributionDescription}
                onChange={handleChange('contributionDescription')}
                multiline
                rows={2}
                disabled={loading}
                helperText={t('common.helpers.contributionUsage')}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <BedroomEditor bedrooms={bedrooms} onChange={setBedrooms} disabled={loading} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <AmenitiesSelector selected={amenities} onChange={setAmenities} disabled={loading} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? `${t('common.actions.save')}...` : house ? t('common.actions.update') : t('common.actions.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
